import { Analytics } from '@util/Analytics';
import { Form } from 'antd';
import React, { FunctionComponent, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { FlowContentBody, QuestionWrapper, ScreenWrapper } from '../styles/Containers';
import { TextInput } from '../styles/TextInput';
import { BodySM, Heading3, HeroTitle } from '../styles/Typography';
import { NavigationProps } from './FlowNavigation';
import { LayoutActions } from './layouts/LayoutActions';

/** A titled list of commitments shown on the expectation-setting screen. */
export type ExpectationList = {
  heading: string;
  items: string[];
};

export type ExpectationSettingLayoutConfig = {
  title: string;
  intro: string;
  promise: ExpectationList;
  ask: ExpectationList;
  closing: string;
  namePrompt: string;
  namePlaceholder?: string;
  primaryButtonTitle: string;
};

export type ExpectationSettingScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.ExpectationSetting,
  ExpectationSettingLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  ExpectationSettingScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ExpectationSettingScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  // The name is a commitment gesture that gates the button; it is intentionally not stored.
  const [name, setName] = useState('');

  const { title, intro, promise, ask, closing, namePrompt, namePlaceholder, primaryButtonTitle } =
    props.layout;
  const trimmedName = name.trim();

  function onPrimaryPress(): void {
    if (trimmedName.length === 0) return;

    Analytics.track({ event: Analytics.Event.o_question_answered, question: props.screenName });

    props.navigation.next(props);
  }

  return (
    <ScreenWrapper>
      <Form>
        <QuestionWrapper>
          <FlowContentBody>
            <TextBlock>
              <HeroTitle>{title}</HeroTitle>
              <BodySM>{intro}</BodySM>
            </TextBlock>
            <ExpectationListBlock list={promise} />
            <ExpectationListBlock list={ask} />
            <TextBlock>
              <BodySM>{closing}</BodySM>
              <BodySM>{namePrompt}</BodySM>
            </TextBlock>
            <Form.Item>
              <TextInput
                type="text"
                autoComplete="given-name"
                value={name}
                placeholder={namePlaceholder}
                aria-label={namePrompt}
                data-testid="expectation-setting-name"
                onChange={({ target: { value } }) => setName(value)}
              />
            </Form.Item>
          </FlowContentBody>
          <LayoutActions
            primaryButtonTitle={primaryButtonTitle}
            onPressPrimaryButton={onPrimaryPress}
            primaryButtonDisabled={trimmedName.length === 0}
          />
        </QuestionWrapper>
      </Form>
    </ScreenWrapper>
  );
};

type ExpectationListBlockProps = { list: ExpectationList };

const ExpectationListBlock: FunctionComponent<ExpectationListBlockProps> = (props) => {
  const { list } = props;
  return (
    <TextBlock>
      <Heading3>{list.heading}</Heading3>
      {list.items.map((item, index) => (
        <BodySM key={index}>• {item}</BodySM>
      ))}
    </TextBlock>
  );
};

const TextBlock = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing._24px};
`;
